import { columnsRequestAdapter } from "./columns-request";
import { listTemplateAdapter } from "./list-template";
import type { ReactNode } from "react";

type ComponentAdapter = {
  acceptsInternalRuntimeProps?: boolean;
  getResolveOptions?: (propPath: string[]) => {
    skipBindFallback?: boolean;
  };
  adaptProps: (
    props: Record<string, unknown>,
    context: {
      mode?: "edit" | "preview";
      runtimeContext?: Record<string, unknown>;
      hasChildren?: boolean;
      renderChildren?: (
        runtimeContext?: Record<string, unknown>,
      ) => ReactNode;
      runRequestEvent: (
        requestEvent: string,
        payload: Record<string, unknown>,
      ) => Promise<unknown>;
    },
  ) => Record<string, unknown>;
};

type ComponentAdapterRegistry = {
  get: (componentName: string) => ComponentAdapter | null;
};

const columnsAdapterComponentNames = new Set([
  "ProTable",
  "SchemaForm",
  "Table",
]);

const runtimePropComponentNames = new Set([
  "ProTable",
  "SchemaForm",
  "FilterColumns",
  "Breadcrumb",
  "Descriptions",
  "Tag",
  "DesensitizationDisplay",
]);

const dataSourceFallbackComponentNames = new Set(["List", "ProTable"]);

const passthroughAdapter: ComponentAdapter = {
  adaptProps: (props) => props,
};

function withRuntimeProps(adapter: ComponentAdapter): ComponentAdapter {
  return {
    ...adapter,
    acceptsInternalRuntimeProps: true,
  };
}

function withDataSourceFallbackSkip(adapter: ComponentAdapter): ComponentAdapter {
  return {
    ...adapter,
    getResolveOptions: (propPath) => {
      const baseOptions = adapter.getResolveOptions?.(propPath) || {};
      if (propPath[0] !== "dataSource") return baseOptions;
      return {
        ...baseOptions,
        skipBindFallback: true,
      };
    },
  };
}

function buildAdapter(componentName: string, adapter: ComponentAdapter) {
  const withDataSourceStrategy = dataSourceFallbackComponentNames.has(componentName)
    ? withDataSourceFallbackSkip(adapter)
    : adapter;
  return runtimePropComponentNames.has(componentName)
    ? withRuntimeProps(withDataSourceStrategy)
    : withDataSourceStrategy;
}

export const defaultComponentAdapterRegistry: ComponentAdapterRegistry = {
  get: (componentName: string) => {
    if (componentName === "List") {
      return buildAdapter(componentName, listTemplateAdapter);
    }
    if (columnsAdapterComponentNames.has(componentName)) {
      return buildAdapter(componentName, columnsRequestAdapter);
    }
    if (runtimePropComponentNames.has(componentName)) {
      return buildAdapter(componentName, passthroughAdapter);
    }
    return null;
  },
};
